import React, { useCallback, useEffect, useState } from "react";
import { Heart, GraduationCap, BookOpen, Home, ArrowRight, AlertTriangle, Loader2, RotateCw, UserPlus } from "lucide-react";
import { pillarApi, pillarErr, label, pillarDot } from "@/lib/pillarApi";
import PillarResidentDetail from "@/components/nds/windows/pillar/PillarResidentDetail";
import PillarResidentDropdown from "@/components/nds/windows/pillar/PillarResidentDropdown";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";

const ICONS = { Heart, GraduationCap, BookOpen, Home };
const OPEN = (a) => a.status !== "staff_assessed";

function ResidentRow({ a, onOpen }) {
  const status = a.homework_status === "overdue" ? "overdue" : a.status === "resident_submitted" ? "resident_submitted" : a.homework_status || a.status;
  return (
    <li>
      <button type="button" onClick={() => onOpen(a)} data-testid={`pillar-row-${a.pillar_id}-${a.resident_id}-${a.id}`}
        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/5 transition-colors">
        <span className="flex-1 min-w-0 truncate text-sm text-nex-silver">{a.resident_name || "Resident"}</span>
        <OpenResidentButton residentId={a.resident_id} name={a.resident_name} testid={`open-resident-pillar-${a.pillar_id}-${a.resident_id}-${a.id}`} />
        <span className="font-mono text-[10px] text-nex-muted">Wk {a.program_week}</span>
        <span className={`font-mono text-[9px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-full border ${
          status === "overdue" ? "border-red-400/50 text-red-300" : status === "resident_submitted" ? "border-nex-gold/50 text-nex-gold" : "border-white/15 text-nex-muted"}`}>
          {label(status)}
        </span>
        {a.urgent && <AlertTriangle className="h-3.5 w-3.5 text-red-300" aria-label="Urgent" />}
        <ArrowRight className="h-3.5 w-3.5 text-nex-muted" />
      </button>
    </li>
  );
}

function PillarCard({ p, rows, onOpen, onAssign }) {
  const Icon = ICONS[p.icon_key] || Heart;
  const count = (s) => rows.filter((a) => a.status === s).length;
  const active = rows.filter(OPEN);
  return (
    <section data-testid={`pillar-card-${p.id}`} className="flex flex-col border border-white/10 rounded-sm bg-white/[0.02] overflow-hidden">
      <div className="h-1" style={{ background: pillarDot(p.color_hex) }} />
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 shrink-0 rounded-full grid place-items-center border border-white/15" style={{ color: pillarDot(p.color_hex) }}><Icon className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <h3 data-testid={`pillar-card-name-${p.id}`} className="text-nex-silver font-semibold leading-tight">{p.name}</h3>
            <p className="text-xs text-nex-muted mt-1 leading-snug">{p.subtitle}</p>
          </div>
          <button type="button" onClick={() => onAssign(p)} data-testid={`pillar-card-assign-${p.id}`} aria-label="Assign a resident"
            className="h-8 w-8 grid place-items-center rounded-sm border border-white/10 text-nex-muted hover:text-nex-gold hover:border-nex-gold/50">
            <UserPlus className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1.5 text-center">
          {[["pending", "Pending"], ["resident_submitted", "Submitted"], ["staff_assessed", "Assessed"]].map(([k, l]) => (
            <div key={k} className="py-2 border border-white/5 rounded-sm">
              <div data-testid={`pillar-card-count-${p.id}-${k}`} className="font-mono text-lg text-nex-gold">{count(k)}</div>
              <div className="font-mono text-[8px] tracking-[0.16em] uppercase text-nex-muted">{l}</div>
            </div>
          ))}
        </div>
        <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">Active homework · {active.length}</div>
      </div>
      <ul data-testid={`pillar-card-list-${p.id}`} className="flex-1 max-h-[480px] overflow-y-auto divide-y divide-white/5 border-t border-white/5">
        {active.length ? active.map((a) => <ResidentRow key={a.id} a={a} onOpen={(x) => onOpen(x, p)} />)
          : <li data-testid={`pillar-card-empty-${p.id}`} className="px-3 py-6 text-center text-xs text-nex-muted">No active homework.</li>}
      </ul>
    </section>
  );
}

function AssignPicker({ pillar, residents, onPick, onClose }) {
  const [q, setQ] = useState("");
  const list = residents.filter((r) => `${r.first_name} ${r.last_name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="pillar-assign-picker" className="p-5 space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-nex-silver font-semibold">Assign · {pillar.name}</h3>
        <button type="button" onClick={onClose} data-testid="pillar-assign-close" className="ml-auto font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold">Back</button>
      </div>
      <input data-testid="pillar-assign-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search active residents…"
        className="w-full px-3 py-2 bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver text-sm rounded-sm" />
      <ul className="max-h-[520px] overflow-y-auto divide-y divide-white/5 border border-white/5 rounded-sm">
        {list.map((r) => (
          <li key={r.id}><button type="button" data-testid={`pillar-assign-pick-${r.id}`} onClick={() => onPick(r)}
            className="w-full text-left px-3 py-2 text-sm text-nex-silver hover:bg-white/5 flex items-center gap-2"><span className="flex-1">{r.first_name} {r.last_name}</span><OpenResidentButton residentId={r.id} name={`${r.first_name} ${r.last_name}`} testid={`open-resident-pick-${r.id}`} /></button></li>
        ))}
        {!list.length && <li className="px-3 py-6 text-center text-xs text-nex-muted">No residents match.</li>}
      </ul>
    </div>
  );
}

export default function SinglePillarContent({ pillarId, focus }) {
  const [state, setState] = useState({ loading: true, pillars: [], assignments: [], error: null });
  const [selected, setSelected] = useState(null);
  const [picking, setPicking] = useState(null);
  const [residents, setResidents] = useState([]);

  const load = useCallback(() => Promise.all([pillarApi.get("/pillars"), pillarApi.get("/pillar-assignments")])
    .then(([p, a]) => setState({ loading: false, pillars: p.pillars, assignments: a.assignments, error: null }))
    .catch((e) => setState((s) => ({ ...s, loading: false, error: pillarErr(e) }))), []);
  useEffect(() => { load(); }, [load]);
  const focusDone = React.useRef(null);
  useEffect(() => {
    const p = focus && focusDone.current !== focus && state.pillars.find((x) => x.id === pillarId);
    if (p) { focusDone.current = focus; setSelected({ residentId: focus.residentId, residentName: focus.residentName, pillar: p }); }
  }, [focus, pillarId, state.pillars]);
  useEffect(() => { pillarApi.get("/residents", { status: "active" }).then((d) => setResidents(d.residents)).catch(() => {}); }, []);

  const openAssign = (p) => setPicking(p);

  if (selected) {
    return <PillarResidentDetail {...selected} pillars={state.pillars} onChanged={load} onBack={() => { setSelected(null); load(); }} />;
  }
  if (picking) {
    return <AssignPicker pillar={picking} residents={residents} onClose={() => setPicking(null)}
      onPick={(r) => { setPicking(null); setSelected({ residentId: r.id, residentName: `${r.first_name} ${r.last_name}`, pillar: picking }); }} />;
  }
  if (state.loading) return <div data-testid="pillar-engine-loading" className="flex-1 flex items-center justify-center gap-2 p-10 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading pillars…</div>;
  if (state.error) {
    return (
      <div data-testid="pillar-engine-error" className="p-10 text-center space-y-3">
        <p className="text-red-300 text-sm">{state.error}</p>
        <button type="button" onClick={load} data-testid="pillar-engine-retry" className="inline-flex items-center gap-2 px-4 py-2 border border-nex-gold/50 text-nex-gold font-mono text-[10px] tracking-[0.2em] uppercase rounded-sm"><RotateCw className="h-3.5 w-3.5" /> Retry</button>
      </div>
    );
  }
  const p = state.pillars.find((x) => x.id === pillarId);
  if (!p) return <div data-testid="pillar-engine-empty" className="p-10 text-center text-nex-muted text-sm">This pillar is not configured yet.</div>;
  const rows = state.assignments.filter((a) => a.pillar_id === p.id);

  return (
    <div data-testid={`pillar-window-${p.id}`} className="p-5 space-y-3">
      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">{rows.length} assignments · {p.name}</span>
        <button type="button" onClick={load} data-testid="pillar-engine-refresh" className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted hover:text-nex-gold"><RotateCw className="h-3 w-3" /> Refresh</button>
      </div>
      <PillarCard p={p} rows={rows} onAssign={openAssign}
        onOpen={(a, pillar) => setSelected({ residentId: a.resident_id, residentName: a.resident_name, pillar })} />
      <PillarResidentDropdown pillarId={p.id} residents={residents} assignments={rows}
        onOpen={(r, name) => setSelected({ residentId: r.id, residentName: name, pillar: p })} />
    </div>
  );
}
